import { useState, useEffect, useMemo } from 'react';
import {
  getSubscriptions,
  sendPaymentLink,
  getCollectionsStatus,
  Subscription,
  CollectionStatus,
} from '../api/client';
import CollectionsNav from '../components/CollectionsNav';

/**
 * Collections → Payers. The backwards lookup the operator asked for: "who pays
 * for whom, and how can one person pay for more than one". Groups every
 * subscription by payer (payerId-index server-side) so each payer's full set of
 * enrolments, across multiple children/plans, reads at a glance.
 *
 * Card state is inferred from the payer's subscriptions (any non-pending sub
 * ⇒ a saved card) so this needs no extra backend field. Payers whose
 * enrolments are all still pending get a one-click "Send link".
 */

const LIVE_STATUSES: CollectionStatus[] = ['active', 'past_due', 'paused', 'completed'];

function poundsLabel(pence: number): string {
  return `£${(pence / 100).toFixed(2)}`;
}

interface PayerGroup {
  payerId: string;
  payerName: string;
  payerEmail: string;
  subs: Subscription[];
  hasMandate: boolean;
  allPending: boolean;
}

export default function Payers() {
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busyPayer, setBusyPayer] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [sentPayers, setSentPayers] = useState<Record<string, boolean>>({});
  const [gcReady, setGcReady] = useState(true);

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    getCollectionsStatus().then(g => setGcReady(!!(g.connected && g.verified))).catch(() => {});
    try {
      setSubs(await getSubscriptions());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load payers');
    } finally {
      setLoading(false);
    }
  }

  const groups = useMemo<PayerGroup[]>(() => {
    const byPayer: Record<string, PayerGroup> = {};
    for (const s of subs) {
      if (!s.payerId) continue;
      let g = byPayer[s.payerId];
      if (!g) {
        g = { payerId: s.payerId, payerName: s.payerName || '(unknown)', payerEmail: s.payerEmail, subs: [], hasMandate: false, allPending: true };
        byPayer[s.payerId] = g;
      }
      g.subs.push(s);
      if (LIVE_STATUSES.includes(s.status)) g.hasMandate = true;
      if (s.status !== 'pending') g.allPending = false;
    }
    return Object.values(byPayer).sort((a, b) => a.payerName.localeCompare(b.payerName));
  }, [subs]);

  const summary = useMemo(() => {
    const active = subs.filter(s => s.status === 'active' || s.status === 'past_due').length;
    const pending = subs.filter(s => s.status === 'pending').length;
    return { payers: groups.length, active, pending };
  }, [subs, groups]);

  // Payers who still need to save a card and can be emailed.
  const needSetup = groups.filter(g => !g.hasMandate && g.payerEmail);

  async function handleSendLink(g: PayerGroup) {
    setBusyPayer(g.payerId);
    setError('');
    try {
      const r = await sendPaymentLink(g.payerId);
      setSentPayers(prev => ({ ...prev, [g.payerId]: true }));
      setSuccess(`Payment link sent to ${r.sentTo}`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send payment link');
    } finally {
      setBusyPayer('');
    }
  }

  async function sendAllNotSetUp() {
    if (!needSetup.length) return;
    setBulkBusy(true);
    setError('');
    const results = await Promise.allSettled(needSetup.map(g => sendPaymentLink(g.payerId)));
    const ok = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.length - ok;
    const sentNow: Record<string, boolean> = {};
    results.forEach((r, i) => { const g = needSetup[i]; if (g && r.status === 'fulfilled') sentNow[g.payerId] = true; });
    setSentPayers(prev => ({ ...prev, ...sentNow }));
    setSuccess(`Sent ${ok} payment link${ok === 1 ? '' : 's'}${failed ? ` · ${failed} failed` : ''}`);
    setBulkBusy(false);
    await load();
    setTimeout(() => setSuccess(''), 5000);
  }

  return (
    <div className="tm-page-shell">
      <CollectionsNav />
      <div className="page-head">
        <div>
          <h1>Payers</h1>
          <p className="subtitle">Who pays for whom. One payer can cover several children or plans, a single saved card per payer collects all the recurring ones.</p>
        </div>
        {needSetup.length > 0 && (
          <div className="head-actions">
            <button className="ebtn ebtn-primary" disabled={bulkBusy || !gcReady} title={!gcReady ? 'Finish payment setup first' : undefined} onClick={sendAllNotSetUp}>
              {bulkBusy ? 'Sending…' : `Send links to ${needSetup.length} payer${needSetup.length === 1 ? '' : 's'}`}
            </button>
          </div>
        )}
      </div>

      {!gcReady && (
        <div className="ealert ealert-info">
          Finish your payment setup, <strong>connect and verify</strong>, before sending payment links. <a className="tm-link" href="/collections">Open setup →</a>
        </div>
      )}
      {error && <div className="ealert ealert-error">{error}</div>}
      {success && <div className="ealert ealert-success">{success}</div>}

      {!loading && (
        <div className="tm-stat-row">
          <div className="tm-stat"><span className="tm-stat-num">{summary.payers}</span><span className="tm-stat-label">Payers</span></div>
          <div className="tm-stat"><span className="tm-stat-num">{summary.active}</span><span className="tm-stat-label">Active enrolments</span></div>
          <div className="tm-stat"><span className="tm-stat-num">{summary.pending}</span><span className="tm-stat-label">Awaiting card</span></div>
        </div>
      )}

      {loading ? (
        <p className="loading">Loading payers…</p>
      ) : groups.length === 0 ? (
        <div className="empty-state"><p>No payers yet, enrol players to create the first one.</p></div>
      ) : (
        <div className="card-surface">
          <table className="tm-table">
            <thead>
              <tr>
                <th>Payer</th>
                <th>Pays for</th>
                <th>Card</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {groups.map(g => (
                <tr key={g.payerId}>
                  <td>
                    <span className="tm-person-name">{g.payerName}</span>
                    {g.payerEmail && <div className="tm-muted" style={{ fontSize: '0.8em' }}>{g.payerEmail}</div>}
                  </td>
                  <td>
                    <ul style={{ margin: 0, paddingLeft: 16 }}>
                      {g.subs.map(s => (
                        <li key={s.subscriptionId} style={{ marginBottom: 4 }}>
                          {s.playerName || s.playerId}, {s.membershipName}
                          <span className="tm-muted"> · {poundsLabel(s.amount)}{s.installmentCount && s.installmentCount > 1 ? `×${s.installmentCount}` : ''}</span>
                          {' '}<span className={`col-pill is-${s.status}`}>{s.status === 'past_due' ? 'past due' : s.status}</span>
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td>
                    {g.hasMandate
                      ? <span className="col-pill is-active">Set up</span>
                      : <span className="col-pill is-pending">Not set up</span>}
                  </td>
                  <td>
                    <div className="tm-actions">
                      {!g.hasMandate && g.payerEmail && (
                        sentPayers[g.payerId]
                          ? <><span className="col-verified">✓ Link sent</span><button className="ebtn ebtn-secondary ebtn-sm" disabled={busyPayer === g.payerId || !gcReady} title={!gcReady ? 'Finish payment setup first' : undefined} onClick={() => handleSendLink(g)}>{busyPayer === g.payerId ? 'Sending…' : 'Resend'}</button></>
                          : <button className="ebtn ebtn-primary ebtn-sm" disabled={busyPayer === g.payerId || !gcReady} title={!gcReady ? 'Finish payment setup first' : undefined} onClick={() => handleSendLink(g)}>{busyPayer === g.payerId ? 'Sending…' : 'Send link'}</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
